import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { submitOffline, buscarAnimalOffline } from '../services/offlineHelpers';
import api from '../services/api';

const TIPOS_EVENTO = [
  { value: 'mortandad', label: 'Mortandad', color: 'bg-red-100 text-red-800', icon: 'M6 18L18 6M6 6l12 12' },
  { value: 'enfermedad', label: 'Enfermedad', color: 'bg-orange-100 text-orange-800', icon: 'M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { value: 'medicamento', label: 'Medicamento', color: 'bg-blue-100 text-blue-800', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { value: 'vacunacion', label: 'Vacunacion', color: 'bg-green-100 text-green-800', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { value: 'lesion', label: 'Lesion', color: 'bg-yellow-100 text-yellow-800', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { value: 'observacion', label: 'Observacion', color: 'bg-purple-100 text-purple-800', icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
  { value: 'otro', label: 'Otro', color: 'bg-gray-100 text-gray-800', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

const GRAVEDADES = [
  { value: 'leve', label: 'Leve', color: 'text-green-700' },
  { value: 'moderada', label: 'Moderada', color: 'text-yellow-700' },
  { value: 'grave', label: 'Grave', color: 'text-orange-700' },
  { value: 'critica', label: 'Critica', color: 'text-red-700' },
];

const EventosCampoPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('inicio'); // inicio, sesion_activa, historial
  const [sesionActiva, setSesionActiva] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New session form
  const [lugar, setLugar] = useState('');
  const [descripcion, setDescripcion] = useState('');

  // New event form
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({
    tipo: '', animal_vid: '', detalle: '', tratamiento: '',
    gravedad: '', observaciones: '',
  });
  const [animalBuscado, setAnimalBuscado] = useState(null);
  const [buscandoAnimal, setBuscandoAnimal] = useState(false);

  useEffect(() => {
    loadSesiones();
  }, []);

  const loadSesiones = async () => {
    if (!user?.establecimiento_id || !navigator.onLine) return;
    try {
      const res = await api.get(`/eventos-campo/sesiones/${user.establecimiento_id}`);
      setSesiones(res.data.sesiones || []);
      // Auto-resume active session
      const activa = (res.data.sesiones || []).find(s => s.estado === 'activa');
      if (activa) {
        setSesionActiva(activa);
        setView('sesion_activa');
        loadEventos(activa.id);
      }
    } catch {}
  };

  const loadEventos = async (sesionId) => {
    try {
      const res = await api.get(`/eventos-campo/eventos/${sesionId}`);
      setEventos(res.data.eventos || []);
    } catch {}
  };

  const handleCrearSesion = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const payload = {
        establecimiento_id: user.establecimiento_id,
        lugar: lugar || null,
        descripcion: descripcion || null,
      };
      const result = await submitOffline('/api/eventos-campo/sesiones', payload);
      if (result.online) {
        setSesionActiva({ id: result.data.sesion_id, lugar: result.data.lugar, estado: 'activa', total_eventos: 0 });
        setView('sesion_activa');
        setSuccess('Sesion de campo iniciada');
      } else {
        setSuccess(result.message);
      }
      setLugar('');
      setDescripcion('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear sesion');
    } finally { setLoading(false); }
  };

  const handleFinalizarSesion = async () => {
    if (!sesionActiva) return;
    setLoading(true);
    try {
      await api.put(`/eventos-campo/sesiones/${sesionActiva.id}/finalizar`);
      setSesionActiva(null);
      setEventos([]);
      setView('inicio');
      setSuccess('Sesion finalizada');
      loadSesiones();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al finalizar');
    } finally { setLoading(false); }
  };

  const buscarAnimal = async () => {
    if (!eventForm.animal_vid.trim()) return;
    setBuscandoAnimal(true);
    setAnimalBuscado(null);
    try {
      const result = await buscarAnimalOffline(eventForm.animal_vid.trim(), user.establecimiento_id);
      setAnimalBuscado(result.animal);
    } catch {
      setAnimalBuscado(null);
    } finally { setBuscandoAnimal(false); }
  };

  const handleRegistrarEvento = async () => {
    if (!eventForm.tipo) {
      setError('Selecciona el tipo de evento');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = {
        sesion_id: sesionActiva.id,
        establecimiento_id: user.establecimiento_id,
        animal_id: animalBuscado?.id || null,
        animal_vid: eventForm.animal_vid || null,
        tipo: eventForm.tipo,
        detalle: eventForm.detalle || null,
        tratamiento: eventForm.tratamiento || null,
        gravedad: eventForm.gravedad || null,
        observaciones: eventForm.observaciones || null,
      };
      const result = await submitOffline('/api/eventos-campo/eventos', payload);
      if (result.online) {
        setSuccess(`Evento "${eventForm.tipo}" registrado`);
        loadEventos(sesionActiva.id);
      } else {
        setSuccess(result.message);
      }
      setEventForm({ tipo: '', animal_vid: '', detalle: '', tratamiento: '', gravedad: '', observaciones: '' });
      setAnimalBuscado(null);
      setShowEventForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar evento');
    } finally { setLoading(false); }
  };

  const tipoInfo = (tipo) => TIPOS_EVENTO.find(t => t.value === tipo) || TIPOS_EVENTO[6];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="eventos-campo-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Eventos de Campo</h1>
          <p className="text-sm text-gray-500">Registra mortandad, enfermedades, medicamentos y mas</p>
        </div>
        <div className="flex items-center gap-2">
          {!navigator.onLine && (
            <span className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-xs font-bold border border-amber-300" data-testid="campo-offline-badge">
              MODO CAMPO
            </span>
          )}
          {view !== 'inicio' && (
            <button onClick={() => { setView('inicio'); setSesionActiva(null); setEventos([]); }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border rounded-lg" data-testid="back-to-sessions">
              Ver sesiones
            </button>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="campo-error">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="campo-success">{success}</div>}

      {/* ─── VISTA INICIO: crear sesion + historial ─── */}
      {view === 'inicio' && (
        <div className="space-y-6">
          {/* Create Session */}
          <div className="bg-white rounded-xl shadow-sm p-6" data-testid="create-session-card">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-green-600">+</span> Nueva Sesion de Campo
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lugar (opcional)</label>
                <input type="text" value={lugar} onChange={e => setLugar(e.target.value)}
                  placeholder="Ej: Potrero 5, Corral principal"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="sesion-lugar" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion (opcional)</label>
                <input type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
                  placeholder="Ej: Recorrida matutina, Vacunacion lote 3"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  data-testid="sesion-descripcion" />
              </div>
            </div>
            <button onClick={handleCrearSesion} disabled={loading}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm transition"
              data-testid="iniciar-sesion-btn">
              {loading ? 'Creando...' : 'Iniciar Sesion'}
            </button>
          </div>

          {/* Session History */}
          {sesiones.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="sesiones-historial">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">Sesiones anteriores ({sesiones.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {sesiones.map(s => (
                  <div key={s.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {s.lugar || 'Sin lugar'} {s.descripcion ? `— ${s.descripcion}` : ''}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {s.fecha_inicio ? new Date(s.fecha_inicio).toLocaleString('es-AR') : ''}
                        {' '}&middot;{' '}
                        <span className={s.estado === 'activa' ? 'text-green-600 font-semibold' : 'text-gray-400'}>{s.estado}</span>
                        {' '}&middot;{' '}{s.total_eventos || 0} eventos
                      </p>
                    </div>
                    {s.estado === 'activa' && (
                      <button onClick={() => { setSesionActiva(s); setView('sesion_activa'); loadEventos(s.id); }}
                        className="px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-xs font-semibold hover:bg-green-200 transition"
                        data-testid={`reanudar-sesion-${s.id}`}>
                        Reanudar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── VISTA SESION ACTIVA ─── */}
      {view === 'sesion_activa' && sesionActiva && (
        <div className="space-y-5">
          {/* Session Header */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center justify-between" data-testid="sesion-activa-header">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-bold text-green-800">Sesion activa</span>
              </div>
              <p className="text-sm text-green-700 mt-1">{sesionActiva.lugar || 'Sin lugar definido'}</p>
            </div>
            <button onClick={handleFinalizarSesion} disabled={loading}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-200 transition disabled:opacity-50"
              data-testid="finalizar-sesion-btn">
              Finalizar Sesion
            </button>
          </div>

          {/* Add Event Button */}
          {!showEventForm && (
            <button onClick={() => setShowEventForm(true)}
              className="w-full py-4 border-2 border-dashed border-green-300 rounded-xl text-green-700 font-semibold hover:bg-green-50 transition flex items-center justify-center gap-2"
              data-testid="agregar-evento-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              Registrar Evento
            </button>
          )}

          {/* Event Form */}
          {showEventForm && (
            <div className="bg-white rounded-xl shadow-lg p-5 space-y-4" data-testid="event-form">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-gray-900">Nuevo Evento</h3>
                <button onClick={() => { setShowEventForm(false); setEventForm({ tipo: '', animal_vid: '', detalle: '', tratamiento: '', gravedad: '', observaciones: '' }); setAnimalBuscado(null); }}
                  className="text-gray-400 hover:text-gray-600" data-testid="close-event-form">&times;</button>
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de evento *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TIPOS_EVENTO.map(t => (
                    <button key={t.value} type="button"
                      onClick={() => setEventForm(prev => ({ ...prev, tipo: t.value }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition flex items-center gap-2 ${
                        eventForm.tipo === t.value ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`event-type-${t.value}`}>
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
                      </svg>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Animal search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Animal (VID / IDE)</label>
                <div className="flex gap-2">
                  <input type="text" value={eventForm.animal_vid}
                    onChange={e => setEventForm(prev => ({ ...prev, animal_vid: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && buscarAnimal()}
                    placeholder="Escanear o ingresar VID..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-green-500"
                    data-testid="event-animal-vid" />
                  <button onClick={buscarAnimal} disabled={buscandoAnimal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    data-testid="event-buscar-animal">
                    {buscandoAnimal ? '...' : 'Buscar'}
                  </button>
                </div>
                {animalBuscado && (
                  <p className="mt-1 text-xs text-green-700">
                    Encontrado: VID {animalBuscado.VID} — {animalBuscado.sexo} — {animalBuscado.raza}
                  </p>
                )}
              </div>

              {/* Severity (for diseases/injuries) */}
              {['enfermedad', 'lesion', 'mortandad'].includes(eventForm.tipo) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gravedad</label>
                  <div className="flex gap-2">
                    {GRAVEDADES.map(g => (
                      <button key={g.value} type="button"
                        onClick={() => setEventForm(prev => ({ ...prev, gravedad: g.value }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition ${
                          eventForm.gravedad === g.value ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`event-gravedad-${g.value}`}>
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Detail & Treatment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Detalle</label>
                  <input type="text" value={eventForm.detalle}
                    onChange={e => setEventForm(prev => ({ ...prev, detalle: e.target.value }))}
                    placeholder="Ej: Diarrea, Pata fracturada..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    data-testid="event-detalle" />
                </div>
                {['enfermedad', 'medicamento', 'vacunacion', 'lesion'].includes(eventForm.tipo) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tratamiento / Medicamento</label>
                    <input type="text" value={eventForm.tratamiento}
                      onChange={e => setEventForm(prev => ({ ...prev, tratamiento: e.target.value }))}
                      placeholder="Ej: Oxitetraciclina 20ml..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      data-testid="event-tratamiento" />
                  </div>
                )}
              </div>

              {/* Observations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea value={eventForm.observaciones}
                  onChange={e => setEventForm(prev => ({ ...prev, observaciones: e.target.value }))}
                  rows="2" placeholder="Notas adicionales..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                  data-testid="event-observaciones" />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowEventForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Cancelar
                </button>
                <button onClick={handleRegistrarEvento} disabled={loading || !eventForm.tipo}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition"
                  data-testid="submit-evento-btn">
                  {loading ? 'Guardando...' : navigator.onLine ? 'Registrar Evento' : 'Guardar Local'}
                </button>
              </div>
            </div>
          )}

          {/* Events List */}
          {eventos.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="eventos-list">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">Eventos registrados ({eventos.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {eventos.map(ev => {
                  const info = tipoInfo(ev.tipo);
                  return (
                    <div key={ev.id} className="p-4 hover:bg-gray-50 transition" data-testid={`evento-${ev.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${info.color}`}>{info.label}</span>
                          {ev.gravedad && (
                            <span className={`text-xs font-semibold ${GRAVEDADES.find(g => g.value === ev.gravedad)?.color || ''}`}>
                              {ev.gravedad.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {ev.fecha_registro ? new Date(ev.fecha_registro).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        {ev.animal_vid && <span className="font-mono font-semibold text-blue-700 mr-2">VID: {ev.animal_vid}</span>}
                        {ev.detalle && <span>{ev.detalle}</span>}
                      </div>
                      {ev.tratamiento && <p className="mt-1 text-xs text-blue-600">Tratamiento: {ev.tratamiento}</p>}
                      {ev.observaciones && <p className="mt-1 text-xs text-gray-500">{ev.observaciones}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {eventos.length === 0 && !showEventForm && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No hay eventos registrados en esta sesion</p>
              <p className="text-xs mt-1">Presiona "Registrar Evento" para comenzar</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventosCampoPage;
